// Data model shared by the browser app and the Cloudflare Worker.
// Every element is a plain JSON object. Elements are immutable in the app: an edit replaces the object.

export type ElementType =
  | "ink"
  | "shape"
  | "line"
  | "text"
  | "note"
  | "card"
  | "image"
  | "frame"
  | "sticker"
  | "layer"
  | "page";

export type FontKey = "hand" | "print" | "marker" | "type" | "serif" | "sans" | "mono";
export type Align = "left" | "center" | "right";
export type Dash = "solid" | "dashed" | "dotted";
export type FillStyle = "none" | "solid" | "hachure" | "cross" | "dots";
export type PenKind = "fountain" | "ballpoint" | "pencil" | "marker" | "highlighter" | "brush";
export type ShapeKind =
  | "rect"
  | "roundrect"
  | "ellipse"
  | "diamond"
  | "triangle"
  | "hexagon"
  | "star"
  | "cloud"
  | "cylinder"
  | "parallelogram"
  | "heart"
  | "speech";
export type HeadKind = "none" | "arrow" | "triangle" | "dot" | "bar" | "diamond";
export type Route = "straight" | "curve" | "elbow";

export interface BaseElement {
  id: string;
  type: ElementType;
  /** top-left corner of the unrotated box, world units */
  x: number;
  y: number;
  w: number;
  h: number;
  /** rotation in radians around the box centre */
  rot: number;
  /** fractional-index key; elements paint in ascending order */
  z: string;
  opacity?: number;
  locked?: boolean;
  groupId?: string | null;
  /** the layer this element lives on; missing or unknown means the base layer */
  layer?: string | null;
  /** last edit, ms since epoch (client clock) */
  t: number;
}

export interface InkElement extends BaseElement {
  type: "ink";
  /** flat [x, y, pressure, x, y, pressure, ...] relative to (x, y) */
  pts: number[];
  color: string;
  size: number;
  pen: PenKind;
  /** input had no real pressure (mouse / finger): pressure is simulated from speed */
  sim?: boolean;
}

export interface ShapeElement extends BaseElement {
  type: "shape";
  kind: ShapeKind;
  stroke: string;
  fill: string;
  fillStyle: FillStyle;
  strokeWidth: number;
  dash: Dash;
  /** 0 = neat, 1 = scrawl */
  rough: number;
  seed: number;
  text: string;
  font: FontKey;
  fontSize: number;
  align: Align;
  textColor: string;
  shadow?: boolean;
}

export interface Endpoint {
  /** id of the element this end is attached to */
  bind?: string | null;
  /** attach point inside the target box, 0..1 in each axis (0.5, 0.5 = centre) */
  ax?: number;
  ay?: number;
  head: HeadKind;
}

export interface LineElement extends BaseElement {
  type: "line";
  /** points relative to (x, y); first and last are the ends, a middle one bends curves */
  pts: [number, number][];
  route: Route;
  start: Endpoint;
  end: Endpoint;
  stroke: string;
  strokeWidth: number;
  dash: Dash;
  rough: number;
  seed: number;
  label: string;
  font: FontKey;
  fontSize: number;
}

export interface TextElement extends BaseElement {
  type: "text";
  text: string;
  font: FontKey;
  fontSize: number;
  color: string;
  align: Align;
  /** width follows the text instead of wrapping */
  autoW: boolean;
  bold?: boolean;
  italic?: boolean;
}

export interface NoteElement extends BaseElement {
  type: "note";
  text: string;
  paper: string;
  font: FontKey;
  fontSize: number;
  textColor: string;
  align: Align;
  tape: string | null;
  shadow?: boolean;
}

export interface CardElement extends BaseElement {
  type: "card";
  /** markdown source; supports [[board links]] and $math$ */
  md: string;
  paper: string;
  accent: string;
  fontSize: number;
  shadow?: boolean;
}

export interface ImageElement extends BaseElement {
  type: "image";
  /** asset id on the server, or a data: URL for local boards */
  src: string;
  natW: number;
  natH: number;
  frame: "none" | "polaroid" | "tape";
  caption?: string;
}

export interface FrameElement extends BaseElement {
  type: "frame";
  title: string;
  color: string;
}

export interface StickerElement extends BaseElement {
  type: "sticker";
  key: string;
}

export type BlendMode =
  | "normal"
  | "multiply"
  | "screen"
  | "overlay"
  | "darken"
  | "lighten"
  | "color-burn"
  | "color-dodge"
  | "soft-light"
  | "difference"
  | "luminosity";

/**
 * A layer, like in Photoshop. Stored as an element so it syncs, undoes and works offline like everything else;
 * its z key orders it among the other layers (x/y/w/h are unused). Elements point to it with `layer`.
 */
export interface LayerElement extends BaseElement {
  type: "layer";
  name: string;
  visible: boolean;
  /** locked layers are drawn but nothing on them can be selected, moved or erased */
  locked?: boolean;
  blend: BlendMode;
}

/** The layer every element without a `layer` field belongs to. */
export const BASE_LAYER = "base";

export type SepiaElement =
  | InkElement
  | ShapeElement
  | LineElement
  | TextElement
  | NoteElement
  | CardElement
  | ImageElement
  | FrameElement
  | StickerElement
  | LayerElement
  | PageElement;

/** Everything that is drawn on the canvas (all elements except layers and pages). */
export type DrawElement = Exclude<SepiaElement, LayerElement | PageElement>;

export type PaperKey = "cream" | "kraft" | "white" | "sage" | "blush" | "walnut" | "slate";
export type PatternKey = "grid" | "dots" | "lines" | "blank" | "iso";

/**
 * One sheet of a paged board (a notebook rather than an endless canvas). A board with at least one page is
 * paged: pages sit one under another, in the order of their y, and what is drawn on a page moves with it.
 */
export interface PageElement extends BaseElement {
  type: "page";
  /** this page's ruling; missing means the board's pattern */
  pattern?: PatternKey | null;
}

/** Paper sizes in world units (CSS pixels at 96 per inch), in their usual orientation. */
export const PAGE_SIZES = {
  letter: { label: "Letter", w: 816, h: 1056 },
  a4: { label: "A4", w: 794, h: 1123 },
  a5: { label: "A5", w: 559, h: 794 },
  slide: { label: "Widescreen", w: 1280, h: 720 }
} as const;
export type PageSizeKey = keyof typeof PAGE_SIZES;

/** Space between pages, in world units. */
export const PAGE_GAP = 40;

export interface BoardBackground {
  paper: PaperKey;
  pattern: PatternKey;
  /** grid spacing in world units */
  size: number;
}

export interface BoardMeta {
  id: string;
  title: string;
  folderId: string | null;
  bg: BoardBackground;
  thumb: string | null;
  starred: boolean;
  trashedAt: number | null;
  shareToken: string | null;
  shareMode: "view" | "edit" | null;
  links: string[];
  elCount: number;
  createdAt: number;
  updatedAt: number;
  /** present on boards opened through a share link */
  ownerName?: string;
  role?: "owner" | "editor" | "viewer";
}

export interface Folder {
  id: string;
  name: string;
  color: string | null;
  sort: number;
  createdAt: number;
}

export interface UserInfo {
  id: string;
  email: string;
  name: string;
  settings: Record<string, unknown>;
  createdAt: number;
}

export const DEFAULT_BG: BoardBackground = { paper: "cream", pattern: "grid", size: 24 };

// ---- sync protocol (WebSocket between the app and a board's Durable Object) ----

/** put an element (full object) or delete it (el = null) */
export interface Op {
  id: string;
  el: SepiaElement | null;
}

export type ClientMessage =
  | { t: "hello"; since: number; cid: string }
  | { t: "ops"; mid: number; ops: Op[] }
  | { t: "presence"; x: number; y: number; tool: string; color: string; laser?: [number, number][] }
  | { t: "ping" };

export type ServerMessage =
  | { t: "sync"; rev: number; full: boolean; els: SepiaElement[]; dels: string[] }
  | { t: "resnapshot" }
  | { t: "ack"; mid: number; rev: number }
  | { t: "ops"; rev: number; from: string; ops: Op[] }
  | { t: "presence"; cid: string; name: string; x: number; y: number; tool: string; color: string; laser?: [number, number][] }
  | { t: "left"; cid: string }
  | { t: "error"; msg: string }
  | { t: "pong" };

export interface Snapshot {
  rev: number;
  els: SepiaElement[];
}

// ---- limits shared by both sides ----
export const LIMITS = {
  maxElementBytes: 256 * 1024,
  maxElementsPerBoard: 60000,
  maxOpsPerMessage: 400,
  maxImageBytes: 8 * 1024 * 1024,
  maxTitle: 120,
  maxThumbBytes: 120 * 1024,
  maxSearchText: 20000
};

const TYPES: ReadonlySet<string> = new Set([
  "ink", "shape", "line", "text", "note", "card", "image", "frame", "sticker", "layer", "page"
]);

const PATTERN_KEYS: ReadonlySet<string> = new Set(["grid", "dots", "lines", "blank", "iso"]);

const BLENDS: ReadonlySet<string> = new Set([
  "normal", "multiply", "screen", "overlay", "darken", "lighten", "color-burn", "color-dodge", "soft-light", "difference", "luminosity"
]);

const num = (v: unknown, lo = -1e7, hi = 1e7) => typeof v === "number" && Number.isFinite(v) && v >= lo && v <= hi;
const str = (v: unknown, max: number) => typeof v === "string" && v.length <= max;
const optStr = (v: unknown, max: number) => v === undefined || v === null || str(v, max);
const color = (v: unknown) => str(v, 40);

function endpointOk(e: unknown): boolean {
  if (!e || typeof e !== "object") return false;
  const p = e as Record<string, unknown>;
  return optStr(p.bind, 64) && (p.ax === undefined || num(p.ax, 0, 1)) && (p.ay === undefined || num(p.ay, 0, 1)) && str(p.head, 16);
}

/**
 * Structural check for an element, used by the server before storing it and by the app before drawing
 * anything that arrived from another device (one malformed element must not break the board for everyone).
 */
export function isPlausibleElement(v: unknown): v is SepiaElement {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const e = v as Record<string, unknown>;
  if (!(str(e.id, 64) && (e.id as string).length > 0 && typeof e.type === "string" && TYPES.has(e.type))) return false;
  if (!(num(e.x) && num(e.y) && num(e.w, 0) && num(e.h, 0) && num(e.rot, -100, 100) && str(e.z, 64))) return false;
  if (e.opacity !== undefined && !num(e.opacity, 0, 1)) return false;
  if (!optStr(e.groupId, 64) || !optStr(e.layer, 64)) return false;
  switch (e.type) {
    case "ink": {
      const pts = e.pts;
      if (!Array.isArray(pts) || pts.length > 90000 || pts.length % 3 !== 0) return false;
      for (const n of pts) if (typeof n !== "number" || !Number.isFinite(n)) return false;
      return color(e.color) && num(e.size, 0, 1000) && str(e.pen, 16);
    }
    case "line": {
      const pts = e.pts;
      if (!Array.isArray(pts) || pts.length < 2 || pts.length > 64) return false;
      for (const p of pts) if (!Array.isArray(p) || p.length !== 2 || !num(p[0]) || !num(p[1])) return false;
      return endpointOk(e.start) && endpointOk(e.end) && color(e.stroke) && num(e.strokeWidth, 0, 200) && str(e.label, 2000) && str(e.route, 16);
    }
    case "shape":
      return str(e.kind, 20) && color(e.stroke) && color(e.fill) && str(e.fillStyle, 16) && num(e.strokeWidth, 0, 200) && str(e.text, 20000) && num(e.fontSize, 1, 2000) && num(e.seed, -1e10, 1e10);
    case "text":
      return str(e.text, 100000) && str(e.font, 16) && num(e.fontSize, 1, 4000) && color(e.color);
    case "note":
      return str(e.text, 20000) && color(e.paper) && num(e.fontSize, 1, 2000) && optStr(e.tape, 40);
    case "card":
      return str(e.md, 100000) && color(e.paper) && color(e.accent) && num(e.fontSize, 1, 400);
    case "image":
      return str(e.src, 200000) && num(e.natW, 0) && num(e.natH, 0) && str(e.frame, 16) && optStr(e.caption, 500);
    case "frame":
      return str(e.title, 200) && color(e.color);
    case "sticker":
      return str(e.key, 64);
    case "layer":
      return str(e.name, 80) && typeof e.visible === "boolean" && (e.locked === undefined || typeof e.locked === "boolean") && typeof e.blend === "string" && BLENDS.has(e.blend);
    case "page":
      return num(e.w, 100, 20000) && num(e.h, 100, 20000) && (e.pattern === undefined || e.pattern === null || (typeof e.pattern === "string" && PATTERN_KEYS.has(e.pattern)));
  }
  return false;
}
